import React, { useState, useRef, useCallback } from "react"
import { db } from "../../utils/firebase"
import moment from "moment"
import "./log-viewer.scss"

// UI components
import Tile from "../../components/structure/tile/tile"
import Table from "../../components/structure/table/table"
import Title from "../../components/structure/title/title"
import Input from "../../components/ui/inputs/input"
import Button from "../../components/ui/button/button"
import Badge from "../../components/ui/badge/badge"
import Log from "../../components/booking/logs/log"
import DiffLog from "../../components/booking/logs/diff-log"

/**
 * Resolve a user UID to a display name, using a cache to avoid duplicate fetches
 */
const resolveUserName = async (uid, cache) => {
	if (!uid) return null
	if (cache.current[uid]) return cache.current[uid]

	// Try staff user first
	const userDoc = await db.doc(`users/${uid}`).get()
	if (userDoc.exists) {
		const { first_name, last_name } = userDoc.data()
		const name = `${first_name} ${last_name}`
		cache.current[uid] = name
		return name
	}

	// Try client collection
	const clientDoc = await db.doc(`clients/${uid}`).get()
	if (clientDoc.exists) {
		const { first_name, last_name } = clientDoc.data()
		const name = `${first_name} ${last_name}`
		cache.current[uid] = name
		return name
	}

	cache.current[uid] = uid
	return uid
}

/**
 * Format a log type string into a readable label
 */
const formatLogType = (type) => {
	if (!type) return ""
	return type
		.replace(/_/g, " ")
		.toLowerCase()
		.split(" ")
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
		.join(" ")
}

// Functional component for the detailed log viewer page
export default function LogViewer() {
	const [reference, setReference] = useState("")
	const [loading, setLoading] = useState(false)
	const [error, setError] = useState("")
	const [booking, setBooking] = useState(null)
	const [activityLogs, setActivityLogs] = useState([])
	const [diffLogs, setDiffLogs] = useState([])
	const [commissionLogs, setCommissionLogs] = useState([])
	const [summary, setSummary] = useState(null)

	// Cache for resolved user names
	const userNameCache = useRef({})

	/**
	 * Look up a booking by reference, trying both string and number forms
	 */
	const findBooking = async (ref) => {
		// Try as-is first
		let result = await db.collection("bookings").where("reference", "==", ref).limit(1).get()

		// If not found and input is numeric, try as integer
		if (result.empty && !isNaN(Number(ref))) {
			result = await db.collection("bookings").where("reference", "==", Number(ref)).limit(1).get()
		}

		if (result.empty) return null

		const doc = result.docs[0]
		return { id: doc.id, ...doc.data() }
	}

	/**
	 * Build the summary statistics from all fetched logs
	 */
	const buildSummary = useCallback(async (allLogs, diffLogsList, commissionLogsList) => {
		// Combine all logs for summary calculations
		const allEntries = [...allLogs, ...diffLogsList, ...commissionLogsList]

		// Total count per group
		const totalCount = allEntries.length

		// Collect unique user IDs
		const userIds = new Set()
		allLogs.forEach((log) => log.user && userIds.add(log.user))
		diffLogsList.forEach((log) => log.user && userIds.add(log.user))
		commissionLogsList.forEach((log) => log.paid_by && userIds.add(log.paid_by))

		// Resolve all user names
		const resolvedUsers = {}
		for (const uid of userIds) {
			resolvedUsers[uid] = await resolveUserName(uid, userNameCache)
		}

		// Date range
		const allTimestamps = allEntries
			.map((log) => log.created?.seconds)
			.filter(Boolean)
			.sort((a, b) => a - b)

		const earliest = allTimestamps.length > 0 ? moment(allTimestamps[0], "X").format("DD/MM/YYYY [at] HH:mm") : null
		const latest = allTimestamps.length > 0 ? moment(allTimestamps[allTimestamps.length - 1], "X").format("DD/MM/YYYY [at] HH:mm") : null

		// Count by log type (activity + diff logs)
		const typeCounts = {}
		;[...allLogs, ...diffLogsList].forEach((log) => {
			if (log.type) {
				const label = formatLogType(log.type)
				typeCounts[label] = (typeCounts[label] || 0) + 1
			}
		})
		if (commissionLogsList.length > 0) {
			typeCounts["Commission Payment"] = commissionLogsList.length
		}

		return {
			totalCount,
			users: resolvedUsers,
			earliest,
			latest,
			typeCounts,
			activityCount: allLogs.length,
			diffCount: diffLogsList.length,
			commissionCount: commissionLogsList.length,
		}
	}, [])

	/**
	 * Main search handler
	 */
	const handleSearch = async () => {
		const trimmed = reference.trim()
		if (!trimmed) return

		setLoading(true)
		setError("")
		setBooking(null)
		setActivityLogs([])
		setDiffLogs([])
		setCommissionLogs([])
		setSummary(null)

		try {
			// Find the booking
			const foundBooking = await findBooking(trimmed)

			if (!foundBooking) {
				setError("No booking found with that reference.")
				setLoading(false)
				return
			}

			// Resolve the agent and client names for the booking header
			const [agentName, clientName] = await Promise.all([
				resolveUserName(foundBooking.agent, userNameCache),
				resolveUserName(foundBooking.client, userNameCache),
			])
			foundBooking.agentName = agentName
			foundBooking.clientName = clientName

			setBooking(foundBooking)

			// Fetch all logs and commission logs in parallel
			const [logDocs, commissionDocs] = await Promise.all([
				db.collection(`bookings/${foundBooking.id}/logs`).orderBy("created", "desc").get(),
				db.collection(`bookings/${foundBooking.id}/commission_logs`).orderBy("created", "desc").get(),
			])

			// Separate standard logs from diff logs
			const allLogs = logDocs.docs.map((doc) => ({ id: doc.id, ...doc.data() }))
			const standardLogs = allLogs.filter((log) => !log.diff)
			const diffs = allLogs.filter((log) => log.diff)

			// Map commission logs
			const commissions = commissionDocs.docs.map((doc) => ({ id: doc.id, ...doc.data() }))

			setActivityLogs(standardLogs)
			setDiffLogs(diffs)
			setCommissionLogs(commissions)

			// Build summary
			const summaryData = await buildSummary(standardLogs, diffs, commissions)
			setSummary(summaryData)
		} catch (err) {
			console.error("Error fetching logs:", err)
			setError("An error occurred while fetching the logs. Please try again.")
		}

		setLoading(false)
	}

	/**
	 * Handle pressing Enter in the search input
	 */
	const handleKeyPress = (e) => {
		if (e.key === "Enter") {
			handleSearch()
		}
	}

	return (
		<Tile fullPage={true}>
			<Title className="flex">
				<h1>Detailed Log Viewer</h1>
			</Title>

			{/* Search bar */}
			<div className="log-viewer__search">
				<Input
					type="text"
					placeholder="Enter booking reference"
					value={reference}
					onChange={setReference}
					onKeyPress={handleKeyPress}
				/>
				<Button
					label="Search"
					onClick={handleSearch}
					loading={loading}
					loadingText="Searching..."
					disabled={loading || !reference.trim()}
				/>
			</div>

			{/* Error message */}
			{error && (
				<div className="log-viewer__error">
					<p>{error}</p>
				</div>
			)}

			{/* Booking header */}
			{booking && (
				<div className="log-viewer__booking-header">
					<div className="log-viewer__booking-header-item">
						<span className="label">Reference</span>
						<span className="value">{booking.reference}</span>
					</div>
					<div className="log-viewer__booking-header-item">
						<span className="label">Client</span>
						<span className="value">{booking.clientName || "Unknown"}</span>
					</div>
					<div className="log-viewer__booking-header-item">
						<span className="label">Agent</span>
						<span className="value">{booking.agentName || "Unknown"}</span>
					</div>
					<div className="log-viewer__booking-header-item">
						<span className="label">Created</span>
						<span className="value">
							{booking.created?.seconds ? moment(booking.created.seconds, "X").format("DD/MM/YYYY [at] HH:mm") : "N/A"}
						</span>
					</div>
				</div>
			)}

			{/* Summary panel */}
			{summary && (
				<div className="log-viewer__summary">
					<h3>Summary</h3>
					<div className="log-viewer__summary-grid">
						<div className="log-viewer__summary-stat">
							<span className="stat-value">{summary.totalCount}</span>
							<span className="stat-label">Total Logs</span>
						</div>
						<div className="log-viewer__summary-stat">
							<span className="stat-value">{Object.keys(summary.users).length}</span>
							<span className="stat-label">Unique Users</span>
						</div>
						<div className="log-viewer__summary-stat">
							<span className="stat-value">{summary.activityCount}</span>
							<span className="stat-label">Activity Logs</span>
						</div>
						<div className="log-viewer__summary-stat">
							<span className="stat-value">{summary.diffCount}</span>
							<span className="stat-label">Change Logs</span>
						</div>
						<div className="log-viewer__summary-stat">
							<span className="stat-value">{summary.commissionCount}</span>
							<span className="stat-label">Commission Logs</span>
						</div>
					</div>

					{/* Date range */}
					{summary.earliest && (
						<div className="log-viewer__summary-range">
							<span>Activity from <strong>{summary.earliest}</strong> to <strong>{summary.latest}</strong></span>
						</div>
					)}

					{/* Users involved */}
					<div className="log-viewer__summary-users">
						<span className="section-label">Users involved:</span>
						<div className="log-viewer__summary-users-list">
							{Object.values(summary.users).map((name, i) => (
								<Badge
									key={i}
									label={name}
									type="INFO"
								/>
							))}
						</div>
					</div>

					{/* Type breakdown */}
					<div className="log-viewer__summary-types">
						<span className="section-label">Breakdown by type:</span>
						<div className="log-viewer__summary-types-list">
							{Object.entries(summary.typeCounts)
								.sort(([, a], [, b]) => b - a)
								.map(([type, count], i) => (
									<span
										key={i}
										className="type-count">
										{type}: <strong>{count}</strong>
									</span>
								))}
						</div>
					</div>
				</div>
			)}

			{/* Activity Logs section */}
			{activityLogs.length > 0 && (
				<div className="log-viewer__section">
					<h2>Activity Logs ({activityLogs.length})</h2>
					<Table
						className="logs-table"
						headings={["Event", "When", "User", "Message"]}>
						{activityLogs.map((log) => (
							<Log
								key={log.id}
								details={log}
							/>
						))}
					</Table>
				</div>
			)}

			{/* Change Logs (Diffs) section */}
			{diffLogs.length > 0 && (
				<div className="log-viewer__section">
					<h2>Change Logs ({diffLogs.length})</h2>
					<Table
						className="logs-table"
						headings={["Event", "When", "User", "Changes"]}>
						{diffLogs.map((log) => (
							<DiffLog
								key={log.id}
								logDetails={log}
							/>
						))}
					</Table>
				</div>
			)}

			{/* Commission Logs section */}
			{commissionLogs.length > 0 && (
				<div className="log-viewer__section">
					<h2>Commission Logs ({commissionLogs.length})</h2>
					<Table
						className="logs-table"
						headings={["Amount", "Payment Type", "Paid By", "Date"]}>
						{commissionLogs.map((log) => (
							<CommissionLogRow
								key={log.id}
								log={log}
								userNameCache={userNameCache}
							/>
						))}
					</Table>
				</div>
			)}

			{/* No results after search */}
			{booking && !loading && activityLogs.length === 0 && diffLogs.length === 0 && commissionLogs.length === 0 && (
				<div className="log-viewer__no-results">
					<p>No logs found for this booking.</p>
				</div>
			)}
		</Tile>
	)
}

/**
 * Inline component for rendering a single commission log row
 */
function CommissionLogRow({ log, userNameCache }) {
	const [userName, setUserName] = useState("")

	React.useEffect(() => {
		if (log.paid_by) {
			resolveUserName(log.paid_by, userNameCache).then(setUserName)
		}
	}, [log.paid_by, userNameCache])

	const logDate = log.created?.seconds ? moment(log.created.seconds, "X").format("DD/MM/YYYY [at] HH:mm:ss") : "N/A"

	const formatPaymentType = (type) => {
		if (!type) return "N/A"
		return type
			.replace(/_/g, " ")
			.split(" ")
			.map((w) => w.charAt(0).toUpperCase() + w.slice(1))
			.join(" ")
	}

	return (
		<tr>
			<td>
				<strong>
					{typeof log.amount === "number"
						? `£${log.amount.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
						: "N/A"}
				</strong>
			</td>
			<td>{formatPaymentType(log.payment_type)}</td>
			<td>{userName || <small>No user attached</small>}</td>
			<td>{logDate}</td>
		</tr>
	)
}
